/**
 * Servicio para gestión de usuarios
 * Sistema Klever Octopus - Betel Natural
 */
import apiClient, { handleApiError, formatApiResponse } from './api';

// Endpoints para usuarios
const ENDPOINTS = {
  USERS: '/users',
  USER_BY_ID: (id) => `/users/${id}`,
  USER_PERMISSIONS: (id) => `/users/${id}/permissions`,
  USER_ROLES: '/users/roles',
  ALL_PERMISSIONS: '/users/permissions',
  USER_STATUS: (id) => `/users/${id}/toggle-status`,
  USER_PASSWORD: (id) => `/users/${id}/reset-password`,
  USER_STATS: '/users/stats'
};

/**
 * Obtener lista de usuarios con paginación y filtros
 */
export const getUsers = async (params = {}) => {
  try {
    const queryParams = {
      skip: params.skip || ((params.page - 1) * params.per_page) || 0,
      limit: params.limit || params.per_page || 20,
      search: params.search || undefined,
      role: params.role || undefined,
      is_active: params.status === 'active' ? true : params.status === 'inactive' ? false : undefined
    };

    // Remover parámetros undefined
    Object.keys(queryParams).forEach(key => queryParams[key] === undefined && delete queryParams[key]);

    const response = await apiClient.get(ENDPOINTS.USERS, { params: queryParams });
    return formatApiResponse(response);
  } catch (error) {
    return handleApiError(error);
  }
};

/**
 * Obtener usuario por ID
 */
export const getUserById = async (id) => {
  try {
    const response = await apiClient.get(ENDPOINTS.USER_BY_ID(id));
    return formatApiResponse(response);
  } catch (error) {
    return handleApiError(error);
  }
};

/**
 * Crear nuevo usuario
 */
export const createUser = async (userData) => {
  try {
    const payload = {
      email: userData.email,
      password: userData.password,
      full_name: `${userData.nombre} ${userData.apellido}`,
      role: userData.role_id, // La API espera el string del rol, no el ID
      phone: userData.telefono || undefined,
      address: userData.address || undefined,
      timezone: userData.timezone || 'America/Bogota',
      language: userData.language || 'es'
    };

    // Campos específicos para vendedores
    if (userData.role === 'sales' || userData.role_id === 'sales') {
      payload.commission_percentage = userData.comision_porcentaje || 0;
      payload.payment_period = userData.tipo_pago === 'mensual' ? 'MONTHLY' :
                               userData.tipo_pago === 'quincenal' ? 'BIWEEKLY' :
                               userData.tipo_pago === 'semanal' ? 'WEEKLY' : 'MONTHLY';
      payload.payment_method = userData.forma_pago === 'deposito' ? 'BANK_TRANSFER' :
                               userData.forma_pago === 'efectivo' ? 'CASH' : 'BANK_TRANSFER';
      payload.bank_name = userData.datos_bancarios?.banco || undefined;
      payload.bank_account = userData.datos_bancarios?.numero_cuenta || undefined;
    }

    // Remover valores undefined
    Object.keys(payload).forEach(key => payload[key] === undefined && delete payload[key]);

    const response = await apiClient.post(ENDPOINTS.USERS, payload);
    return formatApiResponse(response);
  } catch (error) {
    return handleApiError(error);
  }
};

/**
 * Actualizar usuario
 */
export const updateUser = async (id, userData) => {
  try {
    const payload = {
      full_name: `${userData.nombre} ${userData.apellido}`,
      phone: userData.telefono || undefined,
      role_ids: userData.role_id ? [parseInt(userData.role_id)] : undefined, // Enviar como array de IDs
      is_active: userData.is_active,
      address: userData.address || undefined,
      timezone: userData.timezone || undefined,
      language: userData.language || undefined
    };

    // Campos específicos para vendedores
    if (userData.comision_porcentaje || userData.tipo_pago || userData.forma_pago) {
      payload.commission_percentage = userData.comision_porcentaje || 0;
      payload.payment_period = userData.tipo_pago === 'mensual' ? 'MONTHLY' :
                               userData.tipo_pago === 'quincenal' ? 'BIWEEKLY' :
                               userData.tipo_pago === 'semanal' ? 'WEEKLY' : 'MONTHLY';
      payload.payment_method = userData.forma_pago === 'deposito' ? 'BANK_TRANSFER' :
                               userData.forma_pago === 'efectivo' ? 'CASH' : 'BANK_TRANSFER';
      payload.bank_name = userData.datos_bancarios?.banco || undefined;
      payload.bank_account = userData.datos_bancarios?.numero_cuenta || undefined;
    }

    // Remover valores undefined
    Object.keys(payload).forEach(key => payload[key] === undefined && delete payload[key]);

    const response = await apiClient.put(ENDPOINTS.USER_BY_ID(id), payload);

    return formatApiResponse(response);
  } catch (error) {
    return handleApiError(error);
  }
};

/**
 * Eliminar usuario (soft delete)
 */
export const deleteUser = async (id) => {
  try {
    const response = await apiClient.delete(ENDPOINTS.USER_BY_ID(id));
    return formatApiResponse(response);
  } catch (error) {
    return handleApiError(error);
  }
};

/**
 * Cambiar estado del usuario (activar/desactivar)
 */
export const changeUserStatus = async (id, isActive) => {
  try {
    const response = await apiClient.post(ENDPOINTS.USER_STATUS(id));
    return formatApiResponse(response);
  } catch (error) {
    return handleApiError(error);
  }
};

/**
 * Cambiar contraseña del usuario
 */
export const changeUserPassword = async (id, passwordData) => {
  try {
    const response = await apiClient.post(ENDPOINTS.USER_PASSWORD(id), {
      new_password: passwordData.newPassword
    });
    return formatApiResponse(response);
  } catch (error) {
    return handleApiError(error);
  }
};

/**
 * Obtener permisos de un usuario
 */
export const getUserPermissions = async (id) => {
  try {
    const response = await apiClient.get(ENDPOINTS.USER_PERMISSIONS(id));
    return formatApiResponse(response);
  } catch (error) {
    return handleApiError(error);
  }
};

/**
 * Asignar permisos a un usuario
 */
export const assignUserPermissions = async (id, permissions) => {
  try {
    const response = await apiClient.put(ENDPOINTS.USER_PERMISSIONS(id), {
      permissions: permissions
    });
    return formatApiResponse(response);
  } catch (error) {
    return handleApiError(error);
  }
};

/**
 * Obtener roles disponibles para asignar
 */
export const getAvailableRoles = async () => {
  try {
    const response = await apiClient.get(ENDPOINTS.USER_ROLES);
    return formatApiResponse(response);
  } catch (error) {
    return handleApiError(error);
  }
};

/**
 * Obtener todos los permisos disponibles
 */
export const getAvailablePermissions = async () => {
  try {
    const response = await apiClient.get(ENDPOINTS.ALL_PERMISSIONS);
    return formatApiResponse(response);
  } catch (error) {
    return handleApiError(error);
  }
};

/**
 * Validar email único
 */
export const validateUniqueEmail = async (email, excludeUserId = null) => {
  try {
    const response = await apiClient.post('/users/validar-email', {
      email: email,
      exclude_user_id: excludeUserId
    });
    return formatApiResponse(response);
  } catch (error) {
    return handleApiError(error);
  }
};

/**
 * Obtener estadísticas de usuarios
 */
export const getUserStats = async () => {
  try {
    const response = await apiClient.get(ENDPOINTS.USER_STATS);
    return formatApiResponse(response);
  } catch (error) {
    return handleApiError(error);
  }
};

export default {
  getUsers,
  getUserById,
  createUser,
  updateUser,
  deleteUser,
  changeUserStatus,
  changeUserPassword,
  getUserPermissions,
  assignUserPermissions,
  getAvailableRoles,
  getAvailablePermissions,
  validateUniqueEmail,
  getUserStats
};
